import React, { useState } from 'react';
import { Icon, IconButton, Image, Modal, Text } from '@goodfynd/ui';
import { useTheme } from '@mui/material';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';

import { creditCards } from '../../config/images';
import strings from '../../config/strings';
import { useOrderActions } from '../../hooks/useOrderActions';
import type { CreditCard } from '../../stores/models/customer/credit-card';

import {
  StyledCreditAvatar,
  StyledPaymentButtonTextContainer,
  StyledPaymentMethodButton,
  StyledPaymentMethodsContent,
  StyledPaymentMethodsContentBottom,
  StyledPaymentMethodsContentTop,
} from './styles';
import type { PaymentMethodsProps } from './types';

export default function PaymentMethods({
  cards = [],
  setPaymentMethod,
  showCreditForm,
  ...props
}: PaymentMethodsProps) {
  const { palette } = useTheme();
  const [busy, setBusy] = useState('');
  const { removeCreditCard } = useOrderActions();

  const cardImages: any = creditCards[palette.mode];

  const handleRemoveCard =
    (card: CreditCard) => async (e: ClickButtonEvent) => {
      e.stopPropagation();

      const confirmed = confirm(
        `Are you sure you want to permanently remove this card: ${card.label}`
      );

      if (confirmed) {
        setBusy(card.token);
        try {
          await removeCreditCard(card);
        } finally {
          setBusy('');
        }
      }
    };

  return (
    <Modal
      containerCss={{
        maxWidth: 425,
        minWidth: 425,
      }}
      footer={
        <StyledPaymentMethodsContentBottom>
          <StyledPaymentMethodButton
            color="white"
            fullWidth
            onClick={showCreditForm}
          >
            <StyledPaymentButtonTextContainer>
              <Icon name="card" />
              <Text as="subhead2" style={{ marginLeft: 16 }}>
                {strings.labels.addCreditCard}
              </Text>
            </StyledPaymentButtonTextContainer>

            <Icon name="chevron_right" />
          </StyledPaymentMethodButton>
        </StyledPaymentMethodsContentBottom>
      }
      header={
        <Text as="modalHeader">{strings.titles.choosePaymentMethod}</Text>
      }
      {...props}
    >
      <div>
        <StyledPaymentMethodsContent>
          <StyledPaymentMethodsContentTop>
            <Text as="subhead3">{strings.titles.savedCards}</Text>

            <List>
              {cards.map((card) => {
                const image =
                  cardImages.hasOwnProperty(card.cardType) &&
                  cardImages[card.cardType];
                return (
                  <ListItem
                    key={card.id}
                    onClick={() => {
                      setPaymentMethod(card);
                      props.close();
                    }}
                    secondaryAction={
                      <IconButton
                        aria-label="Delete Card"
                        busy={busy === card.id}
                        disabled={!!busy}
                        name="delete"
                        onClick={handleRemoveCard(card)}
                      />
                    }
                    sx={{
                      cursor: 'pointer',
                      paddingLeft: 0,
                    }}
                  >
                    <ListItemAvatar>
                      <StyledCreditAvatar>
                        {!!image ? (
                          <Image
                            alt="Credit card image"
                            height={28}
                            src={image}
                            width={45}
                          />
                        ) : (
                          <Icon name="card" />
                        )}
                      </StyledCreditAvatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Text as="subhead2">{`${card.cardType.toUpperCase()}...${
                          card.lastFour
                        }`}</Text>
                      }
                      secondary={`Exp. ${card.expirationMonth.padStart(
                        2,
                        '0'
                      )}/${card.expirationYear.slice(2, 4)}`}
                    />
                  </ListItem>
                );
              })}
            </List>
          </StyledPaymentMethodsContentTop>
        </StyledPaymentMethodsContent>
      </div>
    </Modal>
  );
}
